/**
 * SummaryFooter — sticky footer with record totals and action buttons.
 *
 * Exported separately so ReviewModal can render it outside the scrollable
 * body area while ReviewStep can render it inline.
 */

import { AlertTriangle } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';

// ============================================================================
// Types
// ============================================================================

export interface SummaryFooterProps {
  totals: {
    create: number;
    update: number;
    /** Rows collapsed into earlier rows of the same import — see SummaryView. */
    batch_dedup: number;
    invalid: number;
    conflicts: number;
    total: number;
  };
  onApprove: () => void;
  onResolveConflicts: () => void;
  onEditMappings?: () => void;
  isApproving: boolean;
  /** Filenames still requiring a unique match column before approval — see
   *  SummaryView's getMatchColumnsRequiredFilenames. Disables Approve while
   *  non-empty since the backend rejects approval in this state. */
  matchColumnsRequiredFilenames?: string[];
  /** Whether the `csv_imports.reimport_block_enabled` kill switch was on at
   *  analysis time — see SummaryView's getReimportBlockEnabled. Defaults to
   *  `true`. When `false`, an operator has turned the block off, so
   *  `matchColumnsRequiredFilenames` no longer disables Approve. */
  reimportBlockEnabled?: boolean;
  /** Count of invalid association mappings (see SummaryView's
   *  invalidAssociations). Advisory only — does NOT disable Approve (a
   *  scoped-read false-positive here must not block a legitimate import).
   *  Surfaced as an un-missable alert next to the Approve action instead. */
  invalidAssociationsCount?: number;
}

// ============================================================================
// Component
// ============================================================================

export function SummaryFooter({
  totals,
  onApprove,
  onResolveConflicts,
  onEditMappings,
  isApproving,
  matchColumnsRequiredFilenames,
  reimportBlockEnabled = true,
  invalidAssociationsCount = 0,
}: SummaryFooterProps) {
  // Batch-deduplicated rows aren't independently valid records — their
  // properties merge into the row they were collapsed into. The Approve
  // button is enabled when there's at least one real create or real update.
  const hasValidRecords = totals.create + totals.update > 0;
  const requiredMatchColumnFilenames = matchColumnsRequiredFilenames ?? [];
  // The backend only rejects approval for a match-column-required file while the
  // reimport_block_enabled kill switch is on — once an operator turns it off, the
  // block no longer applies and this must not disable Approve.
  const matchColumnsRequired =
    requiredMatchColumnFilenames.length > 0 && reimportBlockEnabled;
  const isApproveDisabled =
    isApproving ||
    totals.conflicts > 0 ||
    !hasValidRecords ||
    matchColumnsRequired;

  // "To process" counts records that will hit the CRM — creates plus
  // genuine updates. Batch-deduped rows fold into creates and aren't
  // separately processed, so we don't add them here.
  const toProcess = totals.create + totals.update;

  return (
    <div className='bg-[var(--color-bg-primary)]'>
      {invalidAssociationsCount > 0 && (
        <div className='flex items-start gap-1.5 mb-2 px-3 py-2 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
          <AlertTriangle
            aria-hidden={true}
            className='w-3.5 h-3.5 text-utility-error-600 flex-shrink-0 mt-0.5'
          />
          <span className='text-xs text-utility-error-700'>
            {invalidAssociationsCount} association mapping
            {invalidAssociationsCount === 1 ? '' : 's'}{' '}
            {invalidAssociationsCount === 1 ? 'is' : 'are'} invalid and will be
            skipped. Approving won&apos;t be blocked – fix the mapping{' '}
            {invalidAssociationsCount === 1 ? '' : 's'} first if you want those
            associations included.
          </span>
        </div>
      )}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
        <span className='text-xs text-[var(--color-text-tertiary)]'>
          {totals.total} row{totals.total === 1 ? '' : 's'} analyzed ·{' '}
          {toProcess} to process
          {totals.batch_dedup > 0 && (
            <>
              {' '}
              · {totals.batch_dedup} duplicate
              {totals.batch_dedup === 1 ? '' : 's'} merged
            </>
          )}
        </span>

        <div className='flex items-center gap-2'>
          {onEditMappings && (
            <Button
              color='secondary'
              onClick={onEditMappings}
              isDisabled={isApproving}
              className='flex-1 sm:flex-initial'
            >
              Edit mappings
            </Button>
          )}

          {totals.conflicts > 0 && (
            <Button
              color='secondary'
              onClick={onResolveConflicts}
              isDisabled={isApproving}
              className='flex-1 sm:flex-initial'
            >
              Resolve conflicts
            </Button>
          )}

          <Button
            color='primary'
            onClick={onApprove}
            isDisabled={isApproveDisabled}
            isLoading={isApproving}
            className='flex-1 sm:flex-initial'
          >
            Approve & Import
          </Button>
        </div>
      </div>

      {totals.conflicts > 0 && (
        <p className='text-xs text-[var(--color-text-tertiary)] sm:text-right mt-1'>
          Resolve all conflicts to enable the Approve button
        </p>
      )}

      {!hasValidRecords && totals.conflicts === 0 && (
        <p className='text-xs text-[var(--color-text-tertiary)] sm:text-right mt-1'>
          No valid records to import
        </p>
      )}

      {matchColumnsRequired && (
        <p className='text-xs text-[var(--color-text-tertiary)] sm:text-right mt-1'>
          Mark a unique match column for{' '}
          {requiredMatchColumnFilenames.join(', ')} in the mapping step before
          importing.
        </p>
      )}
    </div>
  );
}
